import { useTranslation } from 'react-i18next';
import { FaBuilding, FaCalendarAlt, FaMapMarkerAlt, FaParking } from 'react-icons/fa';
import { LVGPdata } from '../../../../../Data/Projects/LeVIVO GreenfieldPark/Data_LVGP';
import ProjectShowcasePage, { ProjectShowcaseMetaItem, ProjectShowcaseSlide } from '../../Shared/ProjectShowcasePage';
import levivoLogo from '../../../../../Assets/images/LeVIVO/Logos/LeVivo-white-circle-logo.png';

function splitNarrative(text: string) {
    const sentences = text.match(/[^.!?]+[.!?]*/g)?.map((sentence) => sentence.trim()).filter(Boolean) ?? [text];

    return {
        lead: sentences.slice(0, 2).join(' '),
        body: sentences.slice(2).length ? [sentences.slice(2).join(' ')] : []
    };
}

export default function LeVivoGreenfieldPark() {
    const { i18n } = useTranslation();
    const isFrench = i18n.language.startsWith('fr');
    const project = isFrench ? LVGPdata.fr : LVGPdata.en;
    const heroImage = project.images[3]?.url ?? project.images[0]?.url ?? '';
    const storyImage = project.images[1]?.url ?? heroImage;
    const narrative = splitNarrative(project.content.desc[0]);

    const meta: ProjectShowcaseMetaItem[] = [
        {
            label: isFrench ? 'Ville' : 'City',
            value: project.city,
            icon: FaMapMarkerAlt
        },
        {
            label: isFrench ? 'Année' : 'Year',
            value: String(project.yearBuilt),
            icon: FaCalendarAlt
        },
        {
            label: isFrench ? 'Unités' : 'Units',
            value: project.features[0],
            icon: FaBuilding
        },
        {
            label: isFrench ? 'Stationnement' : 'Parking',
            value: project.features[2],
            icon: FaParking
        }
    ];

    const slides: ProjectShowcaseSlide[] = project.images.slice(0, 5).map((slide, index) => ({
        image: slide.url,
        eyebrow: project.city,
        title:
            index === 0
                ? project.name
                : project.features[index - 1] ?? project.name,
        description: project.content.desc[0]
    }));

    return (
        <ProjectShowcasePage
            theme="levivo"
            logo={levivoLogo}
            logoAlt="LeVIVO"
            logoVariant="mark"
            heroImage={heroImage}
            heroAction={{
                label: isFrench ? 'Visiter le site' : 'View website',
                onClick: () => window.open(project.website, '_blank')
            }}
            heroCaption={project.city}
            introEyebrow="LeVIVO"
            title={project.name}
            subtitle={isFrench ? 'Un projet urbain calme et connecté' : 'A calm and connected urban project'}
            introLead={narrative.lead}
            introBody={narrative.body}
            meta={meta}
            storyEyebrow={project.city}
            storyTitle={
                isFrench
                    ? 'Une implantation résidentielle qui mise sur le voisinage et la proximité.'
                    : 'A residential setting built around neighborhood life and proximity.'
            }
            storyBody={[
                project.content.desc[0],
                isFrench
                    ? `Le projet s’appuie sur ${project.features[1].toLowerCase()} ainsi que ${project.features[2].toLowerCase()} pour offrir une expérience locative cohérente.`
                    : `The project relies on ${project.features[1].toLowerCase()} as well as ${project.features[2].toLowerCase()} to deliver a coherent rental experience.`
            ]}
            storyImage={storyImage}
            storyImageAlt={project.name}
            storyAction={{
                label: isFrench ? 'Voir le projet' : 'View the project',
                onClick: () => window.open(project.website, '_blank')
            }}
            galleryEyebrow={isFrench ? 'Galerie' : 'Gallery'}
            galleryTitle={isFrench ? 'Une présence plus généreuse pour les images' : 'A more generous stage for the imagery'}
            galleryDescription={
                isFrench
                    ? 'La page donne davantage de largeur aux vues du projet pour mieux faire ressortir l’enveloppe du bâtiment et les espaces extérieurs.'
                    : 'The page gives the project views more width so the building envelope and exterior spaces come through more clearly.'
            }
            slides={slides}
            previousSlideLabel={isFrench ? 'Image précédente' : 'Previous image'}
            nextSlideLabel={isFrench ? 'Image suivante' : 'Next image'}
            goToSlideLabel={isFrench ? "Aller à l'image" : 'Go to image'}
            featuresEyebrow={isFrench ? 'Faits saillants' : 'Highlights'}
            featuresTitle={isFrench ? 'LeVIVO Greenfield Park en bref' : 'LeVIVO Greenfield Park at a glance'}
            featuresLead={
                isFrench
                    ? 'Une réalisation résidentielle bien calibrée, portée par une exécution rapide et des choix constructifs clairs.'
                    : 'A well-calibrated residential development supported by quick execution and clear construction choices.'
            }
            features={project.features}
            asideEyebrow="LeVIVO"
            asideTitle={project.name}
            asideSubtitle={project.address}
            asideBody={
                isFrench
                    ? 'Le contenu du projet est désormais correctement relié à ses propres images, son texte, et ses caractéristiques plutôt qu’aux données OVIV.'
                    : 'The project content is now correctly connected to its own imagery, copy, and features instead of OVIV placeholder data.'
            }
            asideAction={{
                label: isFrench ? 'Visiter le site LeVIVO' : 'Visit LeVIVO',
                onClick: () => window.open(project.website, '_blank')
            }}
        />
    );
}
